import React, { useState, useEffect, Fragment } from 'react';
import { salesService } from '../../services/salesService';
import { contactsService } from '../../services/contactsService';
import vegetablesService from '../../services/vegetablesService';
import AddVegetableModal from './AddVegetableModal';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import AddContactModal from '../contacts/AddContactModal';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { businessService } from '../../services/businessService';

export default function CreateOrder({ isOpen, onClose, onOrderCreated }) {
    const [customers, setCustomers] = useState([]);
    const [vegetables, setVegetables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddVegetable, setShowAddVegetable] = useState(false);
    const [showAddCustomer, setShowAddCustomer] = useState(false);
    const [formData, setFormData] = useState({
        customerId: '',
        items: [{ vegetableId: '', vegetable: '', estimatedQuantity: '', unit: 'kg' }],
        notes: ''
    });
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [businessDetails, setBusinessDetails] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            const [customersData, vegetablesData, businessData] = await Promise.all([
                contactsService.getContacts('customer'),
                vegetablesService.getVegetables(),
                businessService.getBusinessDetails()
            ]);
            setCustomers(customersData);
            setVegetables(vegetablesData);
            setBusinessDetails(businessData);
        };
        fetchData();
    }, []);

    useEffect(() => {
        const fetchVehicles = async () => {
            try {
                const vehiclesRef = collection(db, 'vehicles');
                const snapshot = await getDocs(vehiclesRef);
                const vehiclesList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setVehicles(vehiclesList);
            } catch (error) {
                console.error('Error fetching vehicles:', error);
            }
        };
        fetchVehicles();
    }, []);

    const handleAddItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { vegetableId: '', vegetable: '', estimatedQuantity: '', unit: 'kg' }]
        }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        if (field === 'vegetableId') {
            const vegetable = vegetables.find(v => v.id === value);
            newItems[index] = {
                ...newItems[index],
                vegetableId: value,
                vegetable: vegetable.name
            };
        } else {
            newItems[index] = { ...newItems[index], [field]: value };
        }
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const handleVegetableAdded = (newVegetable) => {
        setVegetables(prev => [...prev, newVegetable]);
    };

    const handleCustomerAdded = (newCustomer) => {
        setCustomers(prev => [...prev, newCustomer]);
        setShowAddCustomer(false);
    };

    const removeItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Add validation for at least one vegetable
        const hasValidItems = formData.items.some(item => item.vegetableId && item.estimatedQuantity);
        if (!hasValidItems) {
            alert('Please add at least one vegetable to the order');
            return;
        }

        // Convert box quantities to kg if needed
        const processedItems = formData.items.map(item => {
            if (item.unit === 'box') {
                const quantityPerBox = businessDetails?.quantityPerBox || 25; // Default to 25kg per box
                return {
                    ...item,
                    estimatedQuantity: Number(item.estimatedQuantity) * quantityPerBox,
                    originalQuantity: item.estimatedQuantity,
                    originalUnit: 'box'
                };
            }
            return item;
        });

        setLoading(true);
        try {
            const orderData = {
                ...formData,
                items: processedItems,
                vehicleId: selectedVehicle,
                vehicle: vehicles.find(v => v.id === selectedVehicle)
            };
            await salesService.createOrder(orderData);
            if (onOrderCreated) {
                onOrderCreated();
            }
            // Reset form
            setFormData({
                customerId: '',
                items: [{ vegetableId: '', vegetable: '', estimatedQuantity: '', unit: 'kg' }],
                notes: ''
            });
            handleClose(); // Close modal after successful creation
        } catch (error) {
            console.error('Error creating order:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full sm:max-w-2xl transform overflow-hidden bg-white dark:bg-gray-800 
                                p-4 sm:p-6 text-left align-middle shadow-xl transition-all rounded-t-2xl sm:rounded-2xl">
                                <div className="flex items-center justify-between mb-4">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                                    >
                                        Create New Order
                                    </Dialog.Title>
                                    <button
                                        type="button"
                                        className="text-gray-400 hover:text-gray-500 sm:hidden"
                                        onClick={handleClose}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Customer
                                        </label>
                                        <div className="flex space-x-2">
                                            <select
                                                value={formData.customerId}
                                                onChange={(e) => setFormData(prev => ({ ...prev, customerId: e.target.value }))}
                                                className="flex-1 px-3 py-2 text-base rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                                required
                                            >
                                                <option value="">Select Customer</option>
                                                {customers.map(customer => (
                                                    <option key={customer.id} value={customer.id}>
                                                        {customer.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => setShowAddCustomer(true)}
                                                className="px-2 py-2 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                                            >
                                                <PlusIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Assign Vehicle (Optional)
                                        </label>
                                        <select
                                            value={selectedVehicle}
                                            onChange={(e) => setSelectedVehicle(e.target.value)}
                                            className="w-full px-3 py-2 text-base rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                            >
                                            <option value="">Select a vehicle</option>
                                            {vehicles.map(vehicle => (
                                                <option key={vehicle.id} value={vehicle.id}>
                                                    {vehicle.registrationNumber} - {vehicle.driverName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-4">
                                        {formData.items.map((item, index) => (
                                            <div key={index} className="grid grid-cols-1 sm:grid-cols-12 gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <div className="sm:col-span-5">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Vegetable
                                                    </label>
                                                    <div className="flex space-x-2">
                                                        <select
                                                            value={item.vegetableId}
                                                            onChange={(e) => handleItemChange(index, 'vegetableId', e.target.value)}
                                                            className="flex-1 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                                            required
                                                        >
                                                            <option value="">Select Vegetable</option>
                                                            {vegetables.map(veg => (
                                                                <option key={veg.id} value={veg.id}>
                                                                    {veg.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowAddVegetable(true)}
                                                            className="px-2 py-2 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                                                        >
                                                            <PlusIcon className="h-5 w-5" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="sm:col-span-5">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Quantity
                                                    </label>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <input
                                                            type="number"
                                                            value={item.estimatedQuantity}
                                                            onChange={(e) => handleItemChange(index, 'estimatedQuantity', e.target.value)}
                                                            min="0"
                                                            step="0.0001"
                                                            className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                                            required
                                                        />
                                                        <select
                                                            value={item.unit}
                                                            onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                                                            className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                                        >
                                                            <option value="kg">KG</option>
                                                            <option value="box">Box</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="flex sm:col-span-2 justify-end sm:items-end">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeItem(index)}
                                                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="pt-2">
                                        <button
                                            type="button"
                                            onClick={handleAddItem}
                                            className="w-full p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg 
                                              text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 
                                              flex items-center justify-center"
                                        >
                                            <PlusIcon className="h-6 w-6 mr-2" />
                                            Add Item
                                        </button>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Notes
                                        </label>
                                        <textarea
                                            value={formData.notes}
                                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                            rows={3}
                                            className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>

                                    <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-gray-700 dark:text-gray-300 
                                                bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg
                                                hover:bg-gray-50 dark:hover:bg-gray-600"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-white 
                                                bg-primary-600 rounded-lg border border-transparent
                                                hover:bg-primary-700 
                                                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent 
                                                disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {loading ? 'Creating...' : 'Create Order'}
                                        </button>
                                    </div>
                                </form>

                                <AddVegetableModal
                                    isOpen={showAddVegetable}
                                    onClose={() => setShowAddVegetable(false)}
                                    onVegetableAdded={handleVegetableAdded}
                                />

                                <AddContactModal
                                    isOpen={showAddCustomer}
                                    onClose={() => setShowAddCustomer(false)}
                                    contactType="Customer"
                                    onContactAdded={handleCustomerAdded}
                                />
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}